import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Tournament {
    CLAVE: number;
    NOMBRE: string;
    FECHAINICIO: string;
    FECHAFIN: string;
    CVETORNEO: string;
    CVEBOLICHE?: number;
    NOMBREBOLICHE?: string;
    CIUDADBOLICHE?: string;
}

interface Squad {
    CLAVE: number;
    TURNO: number;
    DIASEMANA_I: string;
    HORARIO_I: string;
    DIFERENCIA: number;
    WLINSCRITOS: number;
    CVEEVENTO: number;
    EVENTNAME?: string;
    gameInfo?: {
        JUGADO: number;
        MISTERY: number;
        PROGRESSIVE: number;
        LINEAS: number | null;
        MESA: string | null;
        LETRA: string | null;
        PAGADO: number;
        SUSPENDIDO: number;
        HCP: number | null;
    } | null;
}

interface PackageWithSelection extends Package {
    selectedSquads?: { [eventId: number]: number[] };
    eventos?: any[];
    items?: any[];
}

interface Package {
    CLAVE: number;
    DESCRIPCION: string;
    COSTO: number;
    PACKAGETYPE: string;
}

const MyRegisteredTournaments = () => {
    const navigate = useNavigate();
    const [player, setPlayer] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
    const [tournamentFilter, setTournamentFilter] = useState<'all' | 'summer' | 'nationals' | 'regionals'>(
        () => (sessionStorage.getItem('myTournamentFilter') as 'all' | 'summer' | 'nationals' | 'regionals') || 'all'
    );
    const handleSetTournamentFilter = (f: 'all' | 'summer' | 'nationals' | 'regionals') => {
        setTournamentFilter(f);
        sessionStorage.setItem('myTournamentFilter', f);
    };
    const filteredTournaments = tournaments.filter(t => {
        const name = t.NOMBRE.toLowerCase();
        if (tournamentFilter === 'summer') return name.includes('summer');
        if (tournamentFilter === 'nationals') return name.includes('national');
        if (tournamentFilter === 'regionals') return !name.includes('summer') && !name.includes('national');
        return true;
    });
    const [currentSquads, setCurrentSquads] = useState<Squad[]>([]);
    const [availableSquads, setAvailableSquads] = useState<{ [eventId: number]: Squad[] }>({});
    const [newPackages, setNewPackages] = useState<PackageWithSelection[]>([]);
    const [availableReentries, setAvailableReentries] = useState<Package[]>([]);
    const [availableSideEvents, setAvailableSideEvents] = useState<Package[]>([]);
    const [availableAddOns, setAvailableAddOns] = useState<Package[]>([]);
    const [showAddPackageModal, setShowAddPackageModal] = useState(false);
    const [showMoveSquadModal, setShowMoveSquadModal] = useState(false);
    const [squadToMove, setSquadToMove] = useState<Squad | null>(null);
    const [availableSquadsForMove, setAvailableSquadsForMove] = useState<Squad[]>([]);
    const [loadingAvailableSquads, setLoadingAvailableSquads] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [newSquadToSelect, setNewSquadToSelect] = useState<number | null>(null);
    const [playerDivision, setPlayerDivision] = useState<any>(null);
    const [groupCode, setGroupCode] = useState('');
    const [isMarathon, setIsMarathon] = useState(false);

    // Package squad selection states
    const [showPackageSquadModal, setShowPackageSquadModal] = useState(false);
    const [currentPackageForSelection, setCurrentPackageForSelection] = useState<PackageWithSelection | null>(null);
    const [packageSquadSelection, setPackageSquadSelection] = useState<{ [eventId: number]: Set<number> }>({});
    const [selectedPackageEvent, setSelectedPackageEvent] = useState<any>(null);
    const [packageEventTurnos, setPackageEventTurnos] = useState<any[]>([]);
    const [checkedInStatus, setCheckedInStatus] = useState<number | null>(null);

    useEffect(() => {
        const loggedInPlayerStr = localStorage.getItem('loggedInPlayer');
        if (!loggedInPlayerStr) {
            navigate('/login');
            return;
        }
        const loggedInPlayer = JSON.parse(loggedInPlayerStr);
        setPlayer(loggedInPlayer);
        loadPlayerTournaments(loggedInPlayer.CLAVE);
    }, [navigate]);

    const loadPlayerTournaments = async (jugadorClave: number) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/jugadores/${jugadorClave}/tournaments`);
            const data = await response.json();
            setTournaments(data);
            if (data.length > 0) {
                setSelectedTournament(data[0]);
                loadTournamentDetails(data[0], jugadorClave);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error loading tournaments:', error);
            setLoading(false);
        }
    };

    const loadTournamentDetails = async (tournament: Tournament, jugadorClave: number) => {
        try {
            // Fetch current squads with full details (event names included from backend)
            const squadsResponse = await fetch(
                `${BACKEND_URL}/api/my-tournaments/squads/${tournament.CLAVE}/${jugadorClave}`
            );
            const squadsData = await squadsResponse.json();


            // Fetch game info for each squad
            if (squadsData && squadsData.length > 0) {
                const squadsWithGameInfo = await Promise.all(
                    squadsData.map(async (squad: Squad) => {
                        try {
                            const gameInfoResponse = await fetch(
                                `${BACKEND_URL}/api/my-tournaments/squad-game-info/${tournament.CLAVE}/${jugadorClave}/${squad.CLAVE}`
                            );
                            const gameInfo = await gameInfoResponse.json();
                            return { ...squad, gameInfo };
                        } catch (error) {
                            console.error(`Error fetching game info for squad ${squad.CLAVE}:`, error);
                            return { ...squad, gameInfo: null };
                        }
                    })
                );
                setCurrentSquads(squadsWithGameInfo);
            } else {
                setCurrentSquads([]);
            }

            // Fetch check-in status
            try {
                const checkinResponse = await fetch(
                    `${BACKEND_URL}/api/my-tournaments/checkin-status/${tournament.CLAVE}/${jugadorClave}`
                );
                const checkinData = await checkinResponse.json();
                setCheckedInStatus(checkinData.checkedIn);
            } catch (error) {
                console.error('Error fetching check-in status:', error);
                setCheckedInStatus(null);
            }

            // Fetch player's division for this tournament
            try {
                const divisionResponse = await fetch(
                    `${BACKEND_URL}/api/my-tournaments/player-division/${tournament.CLAVE}/${jugadorClave}`
                );
                const divisionData = await divisionResponse.json();
                if (divisionData) {
                    
                    setPlayerDivision(divisionData);
                } else {
                    setPlayerDivision(null);
                }
            } catch (error) {
                console.error('Error fetching player division:', error);
                setPlayerDivision(null);
            }

            // Check if player has a marathon package, then fetch reentries accordingly
            let hasMarathon = false;
            try {
                const marathonResponse = await fetch(`${BACKEND_URL}/api/player-main-package/${tournament.CLAVE}/${jugadorClave}`);
                const marathonData = await marathonResponse.json();
                hasMarathon = marathonData.isMarathon || false;
                setIsMarathon(hasMarathon);
            } catch (error) {
                console.error('Error checking marathon package:', error);
            }

            // Fetch available reentries and side events for this tournament
            const reentriesResponse = await fetch(`${BACKEND_URL}/api/reentries?cveTorneo=${tournament.CLAVE}&discountCode=${groupCode || ''}&marathon=${hasMarathon}`);
            const reentriesData = await reentriesResponse.json();

            setAvailableReentries(reentriesData);

            const sideEventsResponse = await fetch(`${BACKEND_URL}/api/sideevents?cveTorneo=${tournament.CLAVE}&discountCode=${groupCode || ''}`);
            const sideEventsData = await sideEventsResponse.json();
            setAvailableSideEvents(sideEventsData);

            const addOnsResponse = await fetch(`${BACKEND_URL}/api/add-ons?cveTorneo=${tournament.CLAVE}`);
            const addOnsData = await addOnsResponse.json();
            setAvailableAddOns(Array.isArray(addOnsData) ? addOnsData : []);
        } catch (error) {
            console.error('Error loading tournament details:', error);
        }
    };

    const handleTournamentSwitch = (tournament: Tournament) => {
        setSelectedTournament(tournament);
        setNewPackages([]);
        setShowAddPackageModal(false);
        setShowMoveSquadModal(false);
        setGroupCode(''); // Reset group code when switching tournaments
        if (player) {
            loadTournamentDetails(tournament, player.CLAVE);
        }
    };

    // Refetch packages when groupCode changes
    useEffect(() => {
        if (selectedTournament?.CLAVE && groupCode !== undefined) {
            const fetchPackages = async () => {
                try {
                    const reentriesResponse = await fetch(
                        `${BACKEND_URL}/api/reentries?cveTorneo=${selectedTournament.CLAVE}&discountCode=${groupCode || ''}&marathon=${isMarathon}`
                    );
                    const reentriesData = await reentriesResponse.json();
                  
                    setAvailableReentries(reentriesData);

                    const sideEventsResponse = await fetch(
                        `${BACKEND_URL}/api/sideevents?cveTorneo=${selectedTournament.CLAVE}&discountCode=${groupCode || ''}`
                    );
                    const sideEventsData = await sideEventsResponse.json();
                    setAvailableSideEvents(sideEventsData);

                    const addOnsResponse = await fetch(`${BACKEND_URL}/api/add-ons?cveTorneo=${selectedTournament.CLAVE}`);
                    const addOnsData = await addOnsResponse.json();
                    setAvailableAddOns(Array.isArray(addOnsData) ? addOnsData : []);
                } catch (error) {
                    console.error('Error fetching packages:', error);
                }
            };
            fetchPackages();
        }
    }, [groupCode, selectedTournament?.CLAVE]);

    const handleOpenMoveSquadModal = async (squad: Squad) => {
        setSquadToMove(squad);
        setShowMoveSquadModal(true);
        setLoadingAvailableSquads(true);
        setAvailableSquadsForMove([]);
        setNewSquadToSelect(null);

        // Fetch available squads for the same event
        if (!selectedTournament || !player || !playerDivision) {
            setLoadingAvailableSquads(false);
            return;
        }

        try {
            // Fetch available squads for the same event and division
            const squadsResponse = await fetch(
                `${BACKEND_URL}/api/my-tournaments/squads/available/${selectedTournament.CLAVE}/${squad.CVEEVENTO}/${playerDivision.CLAVE}/${squad.CLAVE}`
            );
            const squadsData = await squadsResponse.json();
            setAvailableSquadsForMove(squadsData || []);
        } catch (error) {
            console.error('Error loading available squads:', error);
            setAvailableSquadsForMove([]);
        } finally {
            setLoadingAvailableSquads(false);
        }
    };

    const handleSelectSquadInModal = (newSquadId: number) => {
        setNewSquadToSelect(newSquadId);
    };

    const handleSaveSquadChangeFromModal = async () => {
        if (!squadToMove || !newSquadToSelect || !selectedTournament || !player) return;

        setIsSaving(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/my-tournaments/squads/move`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    torneoClave: selectedTournament.CLAVE,
                    jugadorClave: player.CLAVE,
                    oldSquadId: squadToMove.CLAVE,
                    newSquadId: newSquadToSelect
                }),
            });

            const result = await response.json();

            if (result.success) {
                alert('Squad moved successfully!');
                await loadTournamentDetails(selectedTournament, player.CLAVE);
                setShowMoveSquadModal(false);
                setSquadToMove(null);
                setNewSquadToSelect(null);
            } else {
                alert(`Failed to move squad: ${result.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error saving squad change:', error);
            alert('Failed to save squad change. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleViewTournamentResults = async () => {
        if (currentSquads.length === 0) return;

        try {
            // Use first available squad to get cut info
            const firstSquad = currentSquads.find(s => s.EVENTNAME && s.EVENTNAME.toLowerCase().includes('qualifying'));
            if (!firstSquad) {
                alert('No qualifying squads found for this tournament.');
                return;
            }
            const cutInfoResponse = await fetch(
                `${BACKEND_URL}/api/my-tournaments/cut-info/${firstSquad.CLAVE}`
            );
            const cutInfo = await cutInfoResponse.json();

            if (!cutInfo) {
                alert('No results information available for this tournament yet.');
                return;
            }

            // Navigate to results page - it will handle showing all divisions and completion status
            navigate('/cut-results', { state: { cutInfo } });
        } catch (error) {
            console.error('Error loading tournament results:', error);
            alert('Failed to load results. Please try again.');
        }
    };

    const handleViewVIPResults = async () => {
        try {
            // Find first VIP squad
            const vipSquad = currentSquads.find(s => 
                s.EVENTNAME && s.EVENTNAME.toLowerCase().includes('vip')
            );
            
            if (!vipSquad) {
                alert('No VIP squads found for this tournament.');
                return;
            }

            const cutInfoResponse = await fetch(
                `${BACKEND_URL}/api/my-tournaments/cut-info/${vipSquad.CLAVE}`
            );
            const cutInfo = await cutInfoResponse.json();

            if (!cutInfo) {
                alert('No VIP results information available yet.');
                return;
            }

            navigate('/cut-results', { state: { cutInfo } });
        } catch (error) {
            console.error('Error loading VIP results:', error);
            alert('Failed to load VIP results. Please try again.');
        }
    };

    const handleViewFinalsResults = async () => {
        if (!selectedTournament) return;
        
        try {
            // Fetch ALL finals events for this tournament (not just ones player bowled)
            const finalsEventsResponse = await fetch(
                `${BACKEND_URL}/api/my-tournaments/finals-events/${selectedTournament.CLAVE}`
            );
            const allFinalsEventsData = await finalsEventsResponse.json();
            
            if (!allFinalsEventsData || allFinalsEventsData.length === 0) {
                alert('No Finals events found for this tournament.');
                return;
            }

            // Prepare cutInfo from first event (for compatibility)
            const firstEvent = allFinalsEventsData[0];
            const cutInfo = {
                CVEEVENTO: firstEvent.CVEEVENTO,
                CVETORNEO: firstEvent.CVETORNEO,
                CVECORTE: firstEvent.CVECORTE
            };

            // Navigate with all finals events - the results page will show them all
            navigate('/cut-results', { 
                state: { 
                    cutInfo: cutInfo, 
                    allFinalsEvents: allFinalsEventsData 
                } 
            });
        } catch (error) {
            console.error('Error loading Finals results:', error);
            alert('Failed to load Finals results. Please try again.');
        }
    };

    const handleViewResults = async (squad: Squad) => {
        if (!squad.gameInfo || squad.gameInfo.JUGADO !== 2) {
            alert('This squad has not been bowled yet.');
            return;
        }

        try {
            // Get cut info for this squad
            const cutInfoResponse = await fetch(
                `${BACKEND_URL}/api/my-tournaments/cut-info/${squad.CLAVE}`
            );
            const cutInfo = await cutInfoResponse.json();

            if (!cutInfo) {
                alert('No cut information found for this squad.');
                return;
            }

            // Navigate to results page - it will handle showing all divisions and completion status
            navigate('/cut-results', { state: { cutInfo } });
        } catch (error) {
            console.error('Error loading cut info:', error);
            alert('Failed to load results. Please try again.');
        }
    };

    const handleAddPackage = async (pkg: Package) => {
        if (!selectedTournament) return;

        // Fetch package description to get eventos
        try {
            const response = await fetch(
                `${BACKEND_URL}/api/paquetes/descripcion/${selectedTournament.CLAVE}/${pkg.CLAVE}`
            );
            const data = await response.json();

            const packageWithDetails: PackageWithSelection = {
                ...pkg,
                eventos: data.eventos || [],
                items: data.items || [],
                selectedSquads: {}
            };

            setCurrentPackageForSelection(packageWithDetails);
            setPackageSquadSelection({});
            setShowAddPackageModal(false);

            // If package has events, show squad selection modal
            if (data.eventos && data.eventos.length > 0) {
                setSelectedPackageEvent(data.eventos[0]);
                setShowPackageSquadModal(true);

                // Fetch turnos for first event
                const turnosResponse = await fetch(
                    `${BACKEND_URL}/api/turnos/${data.eventos[0].CVEEVENTO}/${selectedTournament.CLAVE}/${playerDivision?.CLAVE || ''}`
                );
                const turnosData = await turnosResponse.json();
                setPackageEventTurnos(turnosData);
            } else {
                // No events, add directly
                setNewPackages(prev => [...prev, packageWithDetails]);
            }
        } catch (error) {
            console.error('Error fetching package details:', error);
        }
    };

    const handlePackageEventChange = async (event: any) => {
        setSelectedPackageEvent(event);
        if (selectedTournament && playerDivision) {
            try {
                const response = await fetch(
                    `${BACKEND_URL}/api/turnos/${event.CVEEVENTO}/${selectedTournament.CLAVE}/${playerDivision.CLAVE}`
                );
                const data = await response.json();
                setPackageEventTurnos(data);
            } catch (error) {
                console.error('Error fetching turnos:', error);
            }
        }
    };

    const handlePackageSquadToggle = (turnoId: number, checked: boolean) => {
        if (!selectedPackageEvent) return;

        setPackageSquadSelection(prev => {
            const eventId = selectedPackageEvent.CVEEVENTO;
            const prevSet = new Set(prev[eventId] || []);
            const maxForEvent = selectedPackageEvent.CANTIDAD || 0;

            if (checked) {
                if (prevSet.size < maxForEvent) {
                    prevSet.add(turnoId);
                }
            } else {
                prevSet.delete(turnoId);
            }

            return { ...prev, [eventId]: prevSet };
        });
    };

    const handleCompletePackageSelection = () => {
        if (!currentPackageForSelection) return;

        // Check all events are satisfied
        const allEventsSatisfied = currentPackageForSelection.eventos?.every(ev => {
            const sel = packageSquadSelection[ev.CVEEVENTO];
            return sel && sel.size === ev.CANTIDAD;
        });

        if (allEventsSatisfied) {
            const packageWithSquads: PackageWithSelection = {
                ...currentPackageForSelection,
                selectedSquads: Object.fromEntries(
                    Object.entries(packageSquadSelection).map(([k, v]) => [k, Array.from(v)])
                )
            };

            setNewPackages(prev => [...prev, packageWithSquads]);
            setShowPackageSquadModal(false);
            setCurrentPackageForSelection(null);
            setPackageSquadSelection({});
        }
    };

    const handleProceedToCheckout = () => {
        if (!selectedTournament || !player || !playerDivision) return;

        // Format packages for checkout
        const formattedPackages = newPackages.map(pkg => ({
            tournamentClave: selectedTournament.CLAVE,
            jugadorClave: player.CLAVE,
            jugadorNombre: player.NOMBRECOMPLETO,
            jugadorTATID: player.FICHANAL,
            jugadorEMAIL: player.EMAIL,
            paqueteClave: pkg.CLAVE,
            paqueteDescripcion: pkg.DESCRIPCION,
            costo: pkg.COSTO,
            cantidad: 1,
            divisionClave: playerDivision.CLAVE,
            divisionDescripcion: playerDivision.DESCRIPCION,
            items: pkg.items || [],
            eventos: (pkg.eventos || []).map((ev: any) => ({
                CVEEVENTO: ev.CVEEVENTO,
                evento: ev.EVENTO,
                turnos: pkg.selectedSquads?.[ev.CVEEVENTO] || [],
                turnosInfo: (pkg.selectedSquads?.[ev.CVEEVENTO] || []).map((turnoId: number) => {
                    const turnoObj = packageEventTurnos.find(t => t.CLAVE === turnoId);
                    return turnoObj ? {
                        TURNO: turnoObj.TURNO,
                        HORARIO_I: turnoObj.HORARIO_I,
                        DIASEMANA_I: turnoObj.DIASEMANA_I,
                        CLAVE: turnoObj.CLAVE,
                        alta: 1,
                        waitlist: 0
                    } : null;
                }).filter(Boolean)
            }))
        }));

        navigate('/checkout', {
            state: {
                tournament: {
                    ...selectedTournament,
                    clave: selectedTournament.CLAVE, // Add lowercase clave for CheckoutClover compatibility
                    cveBoliche: selectedTournament.CVEBOLICHE
                },
                player: player,
                packages: formattedPackages
            }
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading your tournaments...</p>
                </div>
            </div>
        );
    }

    if (!player) return null;

    if (tournaments.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
                {/* Header */}
                <div className="bg-white shadow-2xl border-b-4 border-gray-200">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="bg-gray-50 rounded-2xl p-3 shadow-inner">
                                    <img
                                        src="/logo.avif"
                                        alt="Logo"
                                        className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                                    />
                                </div>
                                <div>
                                    <h1 className="text-lg sm:text-lg font-bold text-gray-900">My Registered Tournaments</h1>
                                    <p className="text-gray-600 text-sm sm:text-base">{player?.NOMBRECOMPLETO}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="self-start sm:self-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 shadow-lg"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>

                {/* Empty State */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="text-center">
                        <p className="text-gray-500 text-md">No tournaments registered yet.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 pb-20">
            {/* Header */}
            <div className="bg-white shadow-2xl border-b-4 border-gray-200 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="bg-gray-50 rounded-2xl p-3 shadow-inner">
                                <img
                                    src="/logo.avif"
                                    alt="Logo"
                                    className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                                />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-xl font-bold text-gray-900">My Registered Tournaments</h1>
                                <p className="text-gray-600 text-sm sm:text-base">{player?.NOMBRECOMPLETO}</p>
                                <p className="text-gray-600 text-sm sm:text-base">TAT ID: {player?.FICHANAL} | {player?.EMAIL}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="self-start sm:self-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 shadow-lg"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>

            {/* Tournament Selector */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="bg-white rounded-2xl shadow-lg p-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">Select Tournament</label>

                    {/* Filter chips */}
                    <div className="flex flex-wrap gap-2 mb-3">
                        {(['all', 'summer', 'nationals', 'regionals'] as const).map(f => (
                            <button
                                key={f}
                                onClick={() => handleSetTournamentFilter(f)}
                                className={`px-3 py-1.5 rounded-lg font-semibold text-xs border-2 transition-all ${
                                    tournamentFilter === f
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
                                }`}
                            >
                                {f === 'all' ? 'All' : f === 'summer' ? 'Summer Classic' : f === 'nationals' ? 'Nationals' : 'Regionals'}
                            </button>
                        ))}
                    </div>

                    {/* Tournament cards — 2 cols on mobile, 3 on desktop */}
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {filteredTournaments.map((tournament) => (
                            <button
                                key={tournament.CLAVE}
                                onClick={() => handleTournamentSwitch(tournament)}
                                className={`p-3 rounded-xl border-2 transition-all text-left ${
                                    selectedTournament?.CLAVE === tournament.CLAVE
                                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-500 shadow-lg'
                                        : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'
                                }`}
                            >
                                <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-1">{tournament.NOMBRE}</h3>
                                <p className="text-xs text-gray-500">
                                    {new Date(tournament.FECHAINICIO).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                            </button>
                        ))}
                        {filteredTournaments.length === 0 && (
                            <div className="col-span-2 lg:col-span-3 text-center py-4 text-sm text-gray-400">No tournaments match this filter.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            {selectedTournament && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Tournament Details Card */}
                    <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 mb-6 relative">
                        {/* Check-in Status Badge */}
                        {checkedInStatus !== null && (
                            <div className="absolute top-4 right-4">
                                {checkedInStatus === 1 ? (
                                    <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-lg text-xs font-semibold">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span>Checked in</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-end gap-0.5 bg-amber-100 text-amber-800 px-2 py-1 rounded-lg text-xs">
                                        <div className="flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-semibold">Not checked in</span>
                                        </div>
                                        <span className="text-amber-600 font-normal">Division to be verified</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex items-start gap-4 mb-6 pr-28 sm:pr-0">
                            <div className="bg-blue-100 rounded-xl p-3">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedTournament.NOMBRE}</h2>
                                {(selectedTournament.NOMBREBOLICHE || selectedTournament.CIUDADBOLICHE) && (
                                    <p className="text-gray-600 mb-2 flex items-center gap-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        {[selectedTournament.NOMBREBOLICHE, selectedTournament.CIUDADBOLICHE].filter(Boolean).join(' - ')}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                                    <span className="flex items-center gap-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        {new Date(selectedTournament.FECHAINICIO).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </span>
                                    <span className="text-gray-400">→</span>
                                    <span className="flex items-center gap-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        {new Date(selectedTournament.FECHAFIN).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </span>
                                </div>
                                {playerDivision && (
                                    <div className="flex items-center gap-2">
                                        <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-bold">
                                            Division: {playerDivision.DESCRIPCION}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Current Squads Section */}
                        <div className="border-t-2 border-gray-200 pt-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Squads I'm Registered In:
                            </h3>
                            {currentSquads.length > 0 && (
                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                    <button
                                        onClick={handleViewTournamentResults}
                                        className="text-blue-600 hover:text-blue-800 font-semibold text-md flex items-center gap-2 hover:underline transition"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        View Qualifiers Results
                                    </button>

                                    {currentSquads.some(s => s.EVENTNAME && s.EVENTNAME.toLowerCase().includes('vip')) && (
                                        <button
                                            onClick={handleViewVIPResults}
                                            className="text-purple-600 hover:text-purple-800 font-semibold text-md flex items-center gap-2 hover:underline transition"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                            </svg>
                                            View VIP Results
                                        </button>
                                    )}

                                    {currentSquads.some(s => s.EVENTNAME && s.EVENTNAME.toLowerCase().includes('round')) && (
                                        <button
                                            onClick={handleViewFinalsResults}
                                            className="text-yellow-600 hover:text-yellow-700 font-semibold text-md flex items-center gap-2 hover:underline transition"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                            </svg>
                                            View Finals Results
                                        </button>
                                    )}
                                </div>
                            )}

                            {currentSquads.length > 0 ? (
                                <div className="space-y-4">
                                    {[...currentSquads]
                                        .sort((a, b) => {
                                            const nameA = (a.EVENTNAME || '').localeCompare(b.EVENTNAME || '');
                                            if (nameA !== 0) return nameA;
                                            return (a.TURNO || a.CLAVE) - (b.TURNO || b.CLAVE);
                                        })
                                        .map((squad) => (
                                        <div key={squad.CLAVE} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-300">
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                                <div className="flex-1">
                                                    
                                                    {squad.EVENTNAME && (
                                                        <div className="mb-2">
                                                            <span className="bg-green-600 text-white px-3 py-1 rounded-lg font-bold text-xs uppercase">
                                                                {squad.EVENTNAME}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <span className="bg-green-600 text-white px-3 py-1 rounded-lg font-bold text-sm">
                                                            Squad {squad.TURNO || squad.CLAVE}
                                                        </span>
                                                        {squad.DIASEMANA_I && (
                                                            <>
                                                                <span className="text-gray-700 font-bold">{squad.DIASEMANA_I}</span>
                                                                <span className="text-gray-600">{squad.HORARIO_I}</span>
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* Game Info Badges and Spots Available */}
                                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                                        {/* Show spots available only if JUGADO is not 1 */}
                                                        {squad.gameInfo?.JUGADO !== 1 && (
                                                            <div className="flex items-center gap-1 text-sm text-gray-600">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                                </svg>
                                                                <span>{squad.DIFERENCIA > 0 ? `${squad.DIFERENCIA} spots available` : 'Full'}</span>
                                                            </div>
                                                        )}

                                                        {/* Bowled Status */}
                                                        {squad.gameInfo && squad.gameInfo.JUGADO !== undefined && (
                                                            <>
                                                                {squad.gameInfo.JUGADO === 2 ? (
                                                                    <button
                                                                        onClick={() => handleViewResults(squad)}
                                                                        className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 rounded font-semibold hover:bg-green-200 transition cursor-pointer"
                                                                        title="Click to view results"
                                                                    >
                                                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                        </svg>
                                                                        Bowled: {squad.gameInfo.LINEAS}
                                                                    </button>
                                                            
                                                                ) : squad.gameInfo.JUGADO === 0 ? (
                                                                    <span className="flex items-center gap-1 bg-red-100 text-red-800 px-2 py-0.5 rounded font-semibold">
                                                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                                        </svg>
                                                                        Not Bowled
                                                                    </span>
                                                                ) : null}
                                                            </>
                                                        )}

                                                        {/* MESA and LETRA */}
                                                        {squad.gameInfo?.MESA && squad.gameInfo?.LETRA && (
                                                            <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-semibold">
                                                                Lane {squad.gameInfo.MESA}{squad.gameInfo.LETRA}
                                                            </span>
                                                        )}

                                                        {/* Mystery - Only show when value is 1 */}
                                                        {squad.gameInfo?.MISTERY === 1 && (
                                                            <span className="flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-semibold">
                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                Mystery
                                                            </span>
                                                        )}

                                                        {/* Progressive - Only show when value is 1 */}
                                                        {squad.gameInfo?.PROGRESSIVE === 1 && (
                                                            <span className="flex items-center gap-1 bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded font-semibold">
                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                Progressive
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Show Move Squad button only if not yet bowled, no lane assignment, and tournament hasn't ended */}
                                                {(!squad.gameInfo || (squad.gameInfo.JUGADO === 0 && !squad.gameInfo.MESA && !squad.gameInfo.LETRA)) && new Date() <= new Date(selectedTournament.FECHAFIN) && (
                                                    <button
                                                        onClick={() => handleOpenMoveSquadModal(squad)}
                                                        className="self-start sm:self-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 text-sm"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                                        </svg>
                                                        Change Squad
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No squad assignments found.</p>
                            )}
                        </div>

                        {/* Add Package Button - Only show if tournament hasn't ended */}
                        {new Date() <= new Date(selectedTournament.FECHAFIN) && (
                            <div className="border-t-2 border-gray-200 mt-6 pt-6">
                                <button
                                    onClick={() => setShowAddPackageModal(true)}
                                    className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-bold transition shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add Re-entry or Side Event
                                </button>
                            </div>
                        )}

                        {/* New Packages Added */}
                        {newPackages.length > 0 && (
                            <div className="border-t-2 border-gray-200 mt-6 pt-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                    New Packages Added
                                </h3>
                                <div className="space-y-3">
                                    {newPackages.map((pkg, index) => (
                                        <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-300 flex items-center justify-between">
                                            <div>
                                                <h4 className="font-bold text-gray-900">{pkg.DESCRIPCION}</h4>
                                                <p className="text-sm text-gray-600">${pkg.COSTO.toFixed(2)}</p>
                                            </div>
                                            <button
                                                onClick={() => setNewPackages(prev => prev.filter((_, i) => i !== index))}
                                                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Floating Checkout Button for New Packages */}
            {newPackages.length > 0 && (
                <button
                    onClick={handleProceedToCheckout}
                    className="fixed bottom-6 right-6 z-50 px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-110 flex items-center gap-3"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Proceed to Checkout ({newPackages.length})
                </button>
            )}

            {/* Add Package Modal */}
            {showAddPackageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full my-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
                        {/* Header */}
                        <div className="flex-shrink-0 bg-white border-b-2 border-gray-200 p-6 rounded-t-3xl">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-gray-900">Add Package</h2>
                                <button
                                    onClick={() => setShowAddPackageModal(false)}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-lg transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Group Code Input */}
                            <div>
                                <label htmlFor="groupCode" className="block text-sm font-bold text-gray-900 mb-2">
                                    Group Code (Optional)
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="groupCode"
                                        value={groupCode}
                                        onChange={(e) => setGroupCode(e.target.value)}
                                        placeholder="Enter group code to reveal special packages"
                                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                                    />
                                    {groupCode && (
                                        <button
                                            onClick={() => setGroupCode('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Re-entries */}
                            {availableReentries.length > 0 && (
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">Re-entries</h3>
                                    <div className="space-y-3">
                                        {availableReentries.map((pkg) => (
                                            <button
                                                key={pkg.CLAVE}
                                                onClick={() => handleAddPackage(pkg)}
                                                className="w-full bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 border-2 border-blue-300 rounded-xl p-4 text-left transition-all hover:shadow-lg"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 text-lg">{pkg.DESCRIPCION}</h4>
                                                        <p className="text-blue-600 font-bold text-xl">${pkg.COSTO.toFixed(2)}</p>
                                                    </div>
                                                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Side Events */}
                            {availableSideEvents.length > 0 && (
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">Side Events</h3>
                                    <div className="space-y-3">
                                        {availableSideEvents.map((pkg) => (
                                            <button
                                                key={pkg.CLAVE}
                                                onClick={() => handleAddPackage(pkg)}
                                                className="w-full bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border-2 border-purple-300 rounded-xl p-4 text-left transition-all hover:shadow-lg"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 text-lg">{pkg.DESCRIPCION}</h4>
                                                        <p className="text-purple-600 font-bold text-xl">${pkg.COSTO.toFixed(2)}</p>
                                                    </div>
                                                    <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Add-ons (shirts, ball paddock, etc.) */}
                            {availableAddOns.length > 0 && (
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">Add-ons</h3>
                                    <div className="space-y-3">
                                        {availableAddOns.map((pkg) => (
                                            <button
                                                key={pkg.CLAVE}
                                                onClick={() => handleAddPackage(pkg)}
                                                className="w-full bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 border-2 border-amber-300 rounded-xl p-4 text-left transition-all hover:shadow-lg"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 text-lg">{pkg.DESCRIPCION}</h4>
                                                        <p className="text-amber-600 font-bold text-xl">${pkg.COSTO.toFixed(2)}</p>
                                                    </div>
                                                    <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {availableReentries.length === 0 && availableSideEvents.length === 0 && availableAddOns.length === 0 && (
                                <p className="text-gray-500 text-center italic py-8">No additional packages available for this tournament.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Package Squad Selection Modal */}
            {showPackageSquadModal && currentPackageForSelection && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 overflow-y-auto">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full my-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 100px)' }}>
                        {/* Header */}
                        <div className="flex-shrink-0 bg-white border-b-2 border-gray-200 p-6 rounded-t-3xl">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Select Squads</h2>
                                    <p className="text-sm text-gray-600 mt-1">{currentPackageForSelection.DESCRIPCION}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowPackageSquadModal(false);
                                        setCurrentPackageForSelection(null);
                                        setPackageSquadSelection({});
                                    }}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-lg transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Package Contents */}
                            {currentPackageForSelection.eventos && currentPackageForSelection.eventos.length > 0 && (
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200">
                                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                        <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                        </svg>
                                        Package Includes:
                                    </h3>
                                    <div className="space-y-2">
                                        {currentPackageForSelection.eventos.map((ev: any) => (
                                            <div key={ev.CVEEVENTO} className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                                                <div className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                                    {ev.CANTIDAD}
                                                </div>
                                                <span className="font-semibold text-gray-800">{ev.EVENTO}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Event Selection Tabs */}
                            {currentPackageForSelection.eventos && currentPackageForSelection.eventos.length > 0 && (
                                <div>
                                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 mb-4">
                                        <p className="text-sm text-amber-900">
                                            <span className="font-bold">📋 Instructions:</span> Select an event tab below and choose your preferred squads. Complete all events to add to cart.
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {currentPackageForSelection.eventos.map((ev: any) => {
                                            const isSelected = selectedPackageEvent?.CVEEVENTO === ev.CVEEVENTO;
                                            const isComplete = packageSquadSelection[ev.CVEEVENTO]?.size === ev.CANTIDAD;
                                            return (
                                                <button
                                                    key={ev.CVEEVENTO}
                                                    className={`px-5 py-3 rounded-xl font-bold text-sm sm:text-base transition-all duration-200 shadow-md relative ${
                                                        isSelected
                                                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white scale-105 shadow-lg'
                                                            : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
                                                    }`}
                                                    onClick={() => handlePackageEventChange(ev)}
                                                >
                                                    {ev.EVENTO}
                                                    {isComplete && (
                                                        <span className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-lg">
                                                            ✓
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Squad Selection for Selected Event */}
                            {selectedPackageEvent && (
                                <div>
                                    <div className="mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 text-white shadow-lg">
                                        <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            {selectedPackageEvent.EVENTO}
                                        </h3>
                                        <p className="text-sm">
                                            Select <span className="font-bold text-yellow-300">{selectedPackageEvent.CANTIDAD}</span> squad(s) |
                                            Currently selected: <span className="font-bold text-yellow-300">{packageSquadSelection[selectedPackageEvent.CVEEVENTO]?.size || 0}</span>
                                        </p>
                                    </div>
                                    <div className="space-y-3">
                                        {packageEventTurnos
                                            .sort((a, b) => a.TURNO - b.TURNO)
                                            .map((turno: any) => {
                                                const checked = packageSquadSelection[selectedPackageEvent.CVEEVENTO]?.has(turno.CLAVE);
                                                const isFull = turno.DIFERENCIA < 0;
                                                const isAlreadyRegistered = currentSquads.some(squad => squad.CLAVE === turno.CLAVE);
                                                const maxForEvent = selectedPackageEvent.CANTIDAD || 0;
                                                const currentSelection = packageSquadSelection[selectedPackageEvent.CVEEVENTO] || new Set();
                                                const disabled = isFull || isAlreadyRegistered || (!checked && currentSelection.size >= maxForEvent);

                                                return (
                                                    <div
                                                        key={turno.TURNO}
                                                        className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl shadow-md border-2 transition-all duration-200 ${
                                                            checked
                                                                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-500 shadow-lg'
                                                                : isAlreadyRegistered
                                                                ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-400 opacity-70'
                                                                : isFull
                                                                ? 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300'
                                                                : disabled
                                                                ? 'bg-gray-100 border-gray-300 opacity-60'
                                                                : 'bg-white border-gray-200 hover:border-indigo-400 hover:shadow-lg'
                                                        }`}
                                                    >
                                                        <div className="flex-1 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className="bg-indigo-600 text-white rounded-lg px-3 py-1 font-bold text-sm min-w-[80px] text-center">
                                                                    Squad {turno.TURNO}
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className="font-bold text-gray-900">{turno.DIASEMANA_I}</span>
                                                                    <span className="text-sm text-gray-600">{turno.HORARIO_I}</span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {isAlreadyRegistered ? (
                                                                    <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                                                        ✓ ALREADY REGISTERED
                                                                    </span>
                                                                ) : isFull ? (
                                                                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                                                        🚫 FULL
                                                                    </span>
                                                                ) : disabled ? (
                                                                    <span className="bg-gray-400 text-white px-3 py-1 rounded-full text-xs font-bold">
                                                                        UNAVAILABLE
                                                                    </span>
                                                                ) : (
                                                                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                                                        {turno.DIFERENCIA} SPOTS LEFT
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {!isFull && !isAlreadyRegistered && (
                                                            <label className="flex items-center justify-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    disabled={disabled}
                                                                    onChange={e => handlePackageSquadToggle(turno.CLAVE, e.target.checked)}
                                                                    className="w-7 h-7 rounded-lg border-2 border-gray-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed"
                                                                />
                                                            </label>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Button Footer */}
                        <div className="flex-shrink-0 bg-white border-t-2 border-gray-200 p-6 rounded-b-3xl">
                            {(() => {
                                const allEventsSatisfied = currentPackageForSelection.eventos?.every((ev: any) => {
                                    const sel = packageSquadSelection[ev.CVEEVENTO];
                                    return sel && sel.size === ev.CANTIDAD;
                                });

                                return (
                                    <button
                                        onClick={handleCompletePackageSelection}
                                        disabled={!allEventsSatisfied}
                                        className={`w-full px-6 py-4 rounded-xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-3 ${
                                            allEventsSatisfied
                                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl'
                                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        {allEventsSatisfied ? 'Add to Cart' : 'Complete Squad Selection'}
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Move Squad Modal */}
            {showMoveSquadModal && squadToMove && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full my-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 100px)' }}>
                        <div className="flex-shrink-0 bg-white border-b-2 border-gray-200 p-6 rounded-t-3xl">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-gray-900">Move Squad</h2>
                                <button
                                    onClick={() => {
                                        setShowMoveSquadModal(false);
                                        setSquadToMove(null);
                                        setNewSquadToSelect(null);
                                    }}
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-lg transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Current Squad Info */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-300">
                                <h3 className="font-bold text-gray-900 mb-2">Current Squad</h3>
                                <div className="flex items-center gap-3">
                                    <span className="bg-blue-600 text-white px-3 py-1 rounded-lg font-bold text-sm">
                                        Squad {squadToMove.TURNO || squadToMove.CLAVE}
                                    </span>
                                    {squadToMove.DIASEMANA_I && (
                                        <>
                                            <span className="text-gray-700 font-bold">{squadToMove.DIASEMANA_I}</span>
                                            <span className="text-gray-600">{squadToMove.HORARIO_I}</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Available Squads */}
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">Select New Squad</h3>
                                <p className="text-sm text-gray-600 mb-4">
                                    Choose a new squad to move to. Click a squad to select it, then click "Save Move" below.
                                </p>

                                {loadingAvailableSquads ? (
                                    <div className="bg-gray-50 rounded-xl p-8 text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
                                        <p className="text-gray-600">Loading available squads...</p>
                                    </div>
                                ) : availableSquadsForMove.length > 0 ? (
                                    <div className="space-y-3 max-h-96 overflow-y-auto">
                                        {availableSquadsForMove.map((squad) => {
                                            const isSelected = newSquadToSelect === squad.CLAVE;
                                            return (
                                                <button
                                                    key={squad.CLAVE}
                                                    onClick={() => handleSelectSquadInModal(squad.CLAVE)}
                                                    className={`w-full border-2 rounded-xl p-4 text-left transition-all hover:shadow-lg ${
                                                        isSelected
                                                            ? 'bg-green-50 border-green-500'
                                                            : 'bg-white border-gray-200 hover:border-green-500'
                                                    }`}
                                                    disabled={squad.DIFERENCIA <= 0}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <span className="bg-indigo-600 text-white px-3 py-1 rounded-lg font-bold text-sm">
                                                                Squad {squad.TURNO}
                                                            </span>
                                                            <div className="flex flex-col">
                                                                <span className="text-gray-700 font-bold">{squad.DIASEMANA_I}</span>
                                                                <span className="text-sm text-gray-600">{squad.HORARIO_I}</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {squad.DIFERENCIA > 0 ? (
                                                                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                                                                    {squad.DIFERENCIA} SPOTS
                                                                </span>
                                                            ) : (
                                                                <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                                                                    FULL
                                                                </span>
                                                            )}
                                                            {isSelected && (
                                                                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 rounded-xl p-8 text-center">
                                        <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <p className="text-gray-600 mb-2">No alternative squads available</p>
                                        <p className="text-sm text-gray-500">
                                            Please contact support to move squads or check back later for availability.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Save Button */}
                            {newSquadToSelect && (
                                <div className="sticky bottom-0 bg-white border-t-2 border-gray-200 pt-6 pb-2 -mx-6 px-6">
                                <button
                                    onClick={handleSaveSquadChangeFromModal}
                                    disabled={isSaving}
                                    className="w-full px-6 py-4 rounded-xl font-bold text-lg bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Save Changes
                                        </>
                                    )}
                                </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default MyRegisteredTournaments;
